/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useMemo, useCallback } from 'react';
import * as paginate from 'paginate-array';
export const useDataList = (props) => {
    const { customFilters = [], customSorts = [], defaultFilters = {}, defaultSort, isInfinite = false, itemRender, pageSize = 100000, } = props;
    const [items, setItems] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortCriteria, setSortCriteria] = useState(defaultSort);
    const [filterCriteria, setFilterCriteria] = useState(defaultFilters);
    const filterFunc = useCallback((item) => {
        if (!filterCriteria && !customFilters) {
            return true;
        }
        let customPassed = true;
        customFilters.forEach((customFilter) => {
            if (filterCriteria[customFilter.name] !== null &&
                filterCriteria[customFilter.name] !== undefined &&
                customFilter.filter(item, filterCriteria[customFilter.name]) === false) {
                customPassed = false;
            }
        });
        if (!customPassed) {
            return false;
        }
        let standardPassed = true;
        for (const propName in item) {
            if (filterCriteria[propName] &&
                filterCriteria[propName] !== '' &&
                (item[propName] === null ||
                    item[propName] === undefined ||
                    item[propName].toString().toLowerCase().indexOf(filterCriteria[propName].toLowerCase()) === -1)) {
                standardPassed = false;
            }
        }
        return standardPassed;
    }, [customFilters, filterCriteria]);
    const maxPages = useMemo(() => {
        const itemsToRender = items.filter(filterFunc);
        return Math.floor(itemsToRender.length / pageSize) + (itemsToRender.length % pageSize > 0 ? 1 : 0);
    }, [filterFunc, items, pageSize]);
    const totalItemCount = useMemo(() => {
        return items.length;
    }, [items.length]);
    const filteredItemCount = useMemo(() => {
        if (!items || items.length === 0) {
            return -1;
        }
        return items.filter(filterFunc).length;
    }, [filterFunc, items]);
    const rawUnpaginatedObjects = useMemo(() => {
        const tempItems = [...items];
        const itemsToRender = tempItems
            .concat()
            .sort((a, b) => {
            var _a, _b, _c, _d;
            const customSort = customSorts.find((item) => item.name === sortCriteria.field);
            if (customSort) {
                return customSort.sort(a, b, sortCriteria.direction);
            }
            const upperSortValue = sortCriteria.direction.toLowerCase() === 'asc' ? 1 : -1;
            if (typeof a[sortCriteria.field] === 'string' || a[sortCriteria.field] instanceof String) {
                return (((_b = (_a = a[sortCriteria.field]) === null || _a === void 0 ? void 0 : _a.toString()) === null || _b === void 0 ? void 0 : _b.toLowerCase()) || '') >
                    (((_d = (_c = b[sortCriteria.field]) === null || _c === void 0 ? void 0 : _c.toString()) === null || _d === void 0 ? void 0 : _d.toLowerCase()) || '')
                    ? upperSortValue
                    : upperSortValue * -1;
            }
            else {
                if (a[sortCriteria.field] > b[sortCriteria.field]) {
                    return upperSortValue;
                }
                else {
                    return upperSortValue * -1;
                }
            }
        })
            .filter(filterFunc);
        return itemsToRender;
    }, [customSorts, filterFunc, items, sortCriteria.direction, sortCriteria.field]);
    const renderedItems = useMemo(() => {
        const tempItems = [...items];
        const itemsToRender = paginate(tempItems
            .concat()
            .sort((a, b) => {
            var _a, _b, _c, _d;
            const customSort = customSorts.find((item) => item.name === sortCriteria.field);
            if (customSort) {
                return customSort.sort(a, b, sortCriteria.direction);
            }
            const upperSortValue = sortCriteria.direction.toLowerCase() === 'asc' ? 1 : -1;
            if (typeof a[sortCriteria.field] === 'string' || a[sortCriteria.field] instanceof String) {
                return (((_b = (_a = a[sortCriteria.field]) === null || _a === void 0 ? void 0 : _a.toString()) === null || _b === void 0 ? void 0 : _b.toLowerCase()) || '') >
                    (((_d = (_c = b[sortCriteria.field]) === null || _c === void 0 ? void 0 : _c.toString()) === null || _d === void 0 ? void 0 : _d.toLowerCase()) || '')
                    ? upperSortValue
                    : upperSortValue * -1;
            }
            else {
                if (a[sortCriteria.field] > b[sortCriteria.field]) {
                    return upperSortValue;
                }
                else {
                    return upperSortValue * -1;
                }
            }
        })
            .filter(filterFunc), isInfinite ? 1 : currentPage, isInfinite ? pageSize * currentPage : pageSize).data;
        return (React.createElement(React.Fragment, null, itemsToRender.length ? (itemsToRender.map((item, index, array) => itemRender(item, index, array))) : (React.createElement(React.Fragment, null))));
    }, [
        currentPage,
        customSorts,
        filterFunc,
        isInfinite,
        itemRender,
        items,
        pageSize,
        sortCriteria.direction,
        sortCriteria.field,
    ]);
    return {
        applySort: (field, direction) => {
            if (direction) {
                setSortCriteria({ field: field, direction: direction });
                setCurrentPage(1);
                return;
            }
            const currentSortField = sortCriteria.field;
            const currentSortDirection = sortCriteria.direction;
            if (currentSortField === field) {
                setSortCriteria({
                    field: field,
                    direction: currentSortDirection === 'asc' ? 'desc' : 'asc',
                });
            }
            else {
                setSortCriteria({ field: field, direction: 'asc' });
            }
            setCurrentPage(1);
        },
        applyFilter: (field, value) => {
            const newFilterCriteria = Object.assign({}, filterCriteria);
            newFilterCriteria[field] = value;
            setCurrentPage(1);
            setFilterCriteria(newFilterCriteria);
        },
        applyFilters: (filters) => {
            const newFilterCriteria = Object.assign({}, filterCriteria);
            filters.forEach((filter) => {
                newFilterCriteria[filter.field] = filter.value;
            });
            setCurrentPage(1);
            setFilterCriteria(newFilterCriteria);
        },
        currentPage: currentPage,
        filterCriteria: filterCriteria,
        filteredItemCount: filteredItemCount,
        maxPages: maxPages,
        paginationControls: {
            goTo: (page) => {
                setCurrentPage(page);
            },
            next: () => {
                setCurrentPage(currentPage + 1);
            },
            previous: () => {
                setCurrentPage(currentPage - 1);
            },
        },
        rawUnpaginatedObjects: rawUnpaginatedObjects,
        renderedItems: renderedItems,
        setItems: setItems,
        sortCriteria: sortCriteria,
        totalItemCount: totalItemCount,
    };
};
//# sourceMappingURL=index.js.map